import { Flex, Tag } from 'antd';
import { FC, MouseEventHandler, useCallback, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { PurpleString } from '../algorithmCondition/coloredLabels/PurpleString';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import translations from './AlgorithmJoins.translations.json';
import styled from 'styled-components';
import { colorYellow } from '@atlas-frontend/design-tokens';
import { useSystemClassifiers } from '../../../configuration';
import { SystemClassifierItemDto } from '../../../systemClassifier';
import { getMapBy } from '../../../common/utils';
import {
  getAlgorithmJoinItemLabels,
  getAlgorithmSourceShortLabel,
} from '../utils';
import { AlgorithmHeader } from '../AlgorithmHeader';
import { AlgorithmJoinFormDataType } from './schema/types';
import { useIsDimensionWithDataType } from './utils';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-joins');
addTranslations(translations);

export interface AlgorithmJoinsProps {
  algorithmId: number;
  onEditButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onEditGraphClick?: MouseEventHandler<HTMLButtonElement>;
  editGraphLabel?: string;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  hasError?: boolean;
  joins?: AlgorithmJoinFormDataType[];
  sources?: AlgorithmSourceFormDataType[];
  isLoading?: boolean;
}

export const AlgorithmJoins: FC<AlgorithmJoinsProps> = (props) => {
  const { algorithmId, isLoading, joins = [], sources = [], ...rest } = props;
  const { t } = useTranslation();
  const {
    JOIN_TYPE: { systemClassifierItems: joinTypes },
  } = useSystemClassifiers('JOIN_TYPE');

  const sourcesById = useMemo(() => getMapBy(sources ?? [], 'id'), [sources]);

  const getSourceLabel = useCallback(
    (algorithmSourceId: number) => {
      const source = sourcesById.get(algorithmSourceId);
      return source ? getAlgorithmSourceShortLabel(source) : '';
    },
    [sourcesById],
  );

  const isDimensionWithDateDataType = useIsDimensionWithDataType('DATE');

  return (
    <AlgorithmHeader
      algorithmId={algorithmId}
      id="algorithm-joins"
      title={t('title')}
      count={joins.length > 0 ? joins.length - 1 : 0}
      isLoading={isLoading ? 'manage' : undefined}
      hideExpand={!joins?.length}
      {...rest}
    >
      {joins.length > 0 && (
        <Flex gap="small">
          <strong>{getSourceLabel(joins[0].algorithmSourceId)}</strong>
          <Flex wrap="wrap" gap="large">
            {joins.slice(1).map((join) => (
              <Flex key={join.id} gap="small" vertical>
                <Flex gap="small">
                  <AlgorithmJoinTypeTag
                    joinTypeCode={join.joinTypeCode}
                    joinTypes={joinTypes}
                  />
                  <strong>{getSourceLabel(join.algorithmSourceId)}</strong>
                </Flex>
                {join.algorithmJoinItems.map((item) => {
                  const { sourceLabel, targetLabel } =
                    getAlgorithmJoinItemLabels(
                      sourcesById,
                      isDimensionWithDateDataType,
                      join,
                      item,
                    );

                  return (
                    <Flex key={item.id} gap="small" align="center">
                      <strong>
                        {'algorithmMappingAlias' in item ? (
                          <PurpleString>{sourceLabel}</PurpleString>
                        ) : (
                          sourceLabel
                        )}
                      </strong>
                      <StyledTag>=</StyledTag>
                      <strong>{targetLabel}</strong>
                    </Flex>
                  );
                })}
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </AlgorithmHeader>
  );
};

export const AlgorithmJoinTypeTag: FC<{
  joinTypeCode: string | null;
  joinTypes: SystemClassifierItemDto[];
}> = ({ joinTypeCode, joinTypes }) => {
  if (!joinTypeCode) return null;
  return (
    <StyledJoinTypeTag>
      {joinTypes.find((joinType) => joinType.code === joinTypeCode)?.name}
    </StyledJoinTypeTag>
  );
};

const StyledTag = styled(Tag)`
  margin: 0;

  .ant-typography {
    height: 100%;
  }
`;

const StyledJoinTypeTag = styled(StyledTag)`
  border-color: ${colorYellow[2]};
  background-color: ${colorYellow[1]};
`;
